import React, { useMemo } from 'react'
import classNames from 'classnames'

import { Icon, IconType } from 'components/atoms/Icon'

import classes from './style.module.css'

type PaginationNumbersProps = {
  page: number
  totalPages: number
  onPageClick(page: number): void
}

type PageItem = { type: 'page'; page: number; isActive: boolean } | { type: 'ellipsis'; key: string }

const range = (from: number, to: number) => Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i)

const PaginationNumbers: React.FC<PaginationNumbersProps> = ({ page, totalPages, onPageClick }) => {
  const renderItem = (item: PageItem) => {
    if (item.type === 'ellipsis') {
      return (
        <span key={item.key} className="text-muted small" style={{ padding: '0 2px' }}>
          ...
        </span>
      )
    }

    return (
      <button
        key={item.page}
        type="button"
        className={classNames(item.isActive ? 'btn btn-primary btn-sm' : 'btn btn-outline-primary btn-sm', classes.pageButton)}
        disabled={item.isActive}
        onClick={() => onPageClick(item.page)}
      >
        {item.page}
      </button>
    )
  }

  const items: PageItem[] = useMemo(() => {
    if (!totalPages || totalPages <= 1) return [{ type: 'page', page: 1, isActive: true }]

    const windowSize = 2
    const left = Math.max(2, page - windowSize)
    const right = Math.min(totalPages - 1, page + windowSize)
    const result: PageItem[] = [{ type: 'page', page: 1, isActive: page === 1 }]

    if (left > 2) result.push({ type: 'ellipsis', key: 'left' })

    range(left, right).forEach((p) => result.push({ type: 'page', page: p, isActive: p === page }))

    if (right < totalPages - 1) result.push({ type: 'ellipsis', key: 'right' })

    result.push({ type: 'page', page: totalPages, isActive: page === totalPages })

    return result
  }, [page, totalPages])

  const mobileItems: PageItem[] = useMemo(() => {
    const visiblePages = 4
    const safeTotalPages = totalPages || 1
    const start = Math.min(Math.max(1, page), Math.max(1, safeTotalPages - visiblePages + 1))
    const end = Math.min(safeTotalPages, start + visiblePages - 1)

    return range(start, end).map((p) => ({ type: 'page' as const, page: p, isActive: p === page }))
  }, [page, totalPages])

  return (
    <>
      <div className={classNames(classes.pages, classes.desktopPages)}>{items.map(renderItem)}</div>
      <div className={classNames(classes.pages, classes.mobilePages)}>{mobileItems.map(renderItem)}</div>
    </>
  )
}

export type PaginationBarProps = {
  pagination:
    | {
        page: number
        totalPages: number
        hasNextPage: boolean
        hasPreviousPage: boolean
      }
    | null
    | undefined
  pageSize: number
  pageSizeOptions: readonly number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  className?: string
}

export const PaginationBar: React.FC<PaginationBarProps> = ({ pagination, pageSize, pageSizeOptions, onPageChange, onPageSizeChange, className }) => {
  if (!pagination) return null

  return (
    <div className={classNames('container', classes.bar, className)}>
      <div className={classes.barContent}>
        <button
          className={classNames('btn btn-outline-primary btn-sm', classes.pageButton, classes.navButton)}
          type="button"
          disabled={!pagination.hasPreviousPage}
          onClick={() => onPageChange(pagination.page - 1)}
          aria-label="Предыдущая страница"
        >
          <Icon type={IconType.Arrow} width="12px" height="12px" className={classes.prevIcon} />
        </button>
        <PaginationNumbers page={pagination.page} totalPages={pagination.totalPages || 1} onPageClick={onPageChange} />
        <button
          className={classNames('btn btn-outline-primary btn-sm', classes.pageButton, classes.navButton)}
          type="button"
          disabled={!pagination.hasNextPage}
          onClick={() => onPageChange(pagination.page + 1)}
          aria-label="Следующая страница"
        >
          <Icon type={IconType.Arrow} width="12px" height="12px" />
        </button>
        <div className={classes.pageSizeOptions}>
          {pageSizeOptions.map((size) => (
            <button
              key={size}
              type="button"
              className={`btn btn-link p-0 ${pageSize === size ? 'font-weight-bold text-primary' : 'text-muted'}`}
              onClick={() => onPageSizeChange(size)}
            >
              {size}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default PaginationBar
